/**
 * CampaignClonePage — form-only dash feature (FS-820).
 *
 * UX goal: duplicate an existing campaign with facet-level control over what
 * gets copied, plus an optional creative-name prefix. Operators most often
 * want a one-click clone with a rename — everything else defaults to "on".
 *
 * Pipeline:
 *   1. Pick source campaign       (combobox, sourced from /api/v1/campaign-dashboard/list)
 *   2. Edit new name              (defaults to "Copy of <source>")
 *   3. Tick facet checkboxes      (all on by default, 6 facets)
 *   4. Optional creative prefix   (surfaces the typeahead only when Creatives is on)
 *   5. Clone                       (POST /api/v1/campaign-clone/:id)
 *   6. Success toast + redirect    (via TanStack Router; stub link when no detail page)
 */
import {
	useEffect,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import { Link } from "@tanstack/react-router";
import {
	CLONE_FACETS,
	DEFAULT_FACETS,
	defaultClonedName,
	validateCreativePrefix,
	validateNewName,
	validateSourceId,
	type FacetFlags,
	type FacetId,
	type SourceCampaign,
} from "./api";
import { useCampaignList, useCloneCampaign } from "./hooks";

// ---------------------------------------------------------------------------
// DESIGN.md class tokens — kept inline so the JSX stays scan-able.
// Palette: emerald primary, sky accent, zinc neutrals. Light theme only.
// ---------------------------------------------------------------------------

const CARD = "bg-white border border-zinc-200 rounded-lg shadow-sm p-6";
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const INPUT =
	"w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400";
const INPUT_ERR =
	"w-full border border-red-400 rounded-md px-3 py-2 text-sm focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none placeholder:text-zinc-400";
const HELPER = "text-xs text-zinc-500 mt-1";
const ERROR_TEXT = "text-xs text-red-600 mt-1";
const PRIMARY_BTN =
	"bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";
const SECONDARY_BTN =
	"bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-md px-4 py-2 text-sm font-medium";
const HEADLINE =
	"text-2xl font-semibold bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent";
const SUBTITLE = "text-sm text-zinc-500 mt-1";
const CAPTION_LABEL = "text-xs text-zinc-500 uppercase tracking-wider";

interface FormErrors {
	source?: string;
	name?: string;
	creativePrefix?: string;
}

function SourcePicker({
	value,
	onChange,
	items,
	loading,
	error,
}: {
	value: string;
	onChange: (id: string) => void;
	items: readonly SourceCampaign[];
	loading: boolean;
	error?: string;
}) {
	if (loading) {
		return (
			<div
				className="h-9 bg-zinc-100 animate-pulse rounded-md"
				data-testid="campaign-clone-sources-loading"
				aria-label="Loading campaigns"
			/>
		);
	}
	return (
		<select
			id="campaign-clone-source"
			className={error ? INPUT_ERR : INPUT}
			value={value}
			onChange={(e) => onChange(e.currentTarget.value)}
			data-testid="campaign-clone-source-select"
			aria-invalid={error ? "true" : "false"}
		>
			<option value="">Select a campaign.</option>
			{items.map((c) => (
				<option key={c.id} value={c.id}>
					{c.name}
					{c.vertical ? ` \u2014 ${c.vertical}` : ""}
				</option>
			))}
		</select>
	);
}

function FacetCheckbox({
	id,
	label,
	checked,
	onChange,
}: {
	id: FacetId;
	label: string;
	checked: boolean;
	onChange: (v: boolean) => void;
}) {
	return (
		<label
			className="flex items-start gap-3 p-3 rounded-md border border-zinc-200 hover:bg-zinc-50 cursor-pointer transition-colors"
			data-testid={`campaign-clone-facet-${id}`}
		>
			<input
				type="checkbox"
				className="mt-0.5 accent-emerald-500"
				checked={checked}
				onChange={(e) => onChange(e.currentTarget.checked)}
				data-testid={`campaign-clone-facet-${id}-input`}
			/>
			<span className="text-sm font-medium text-zinc-700">{label}</span>
		</label>
	);
}

export function CampaignClonePage() {
	const listQuery = useCampaignList();
	const cloneMut = useCloneCampaign();

	// Form state ------------------------------------------------------------
	const [sourceId, setSourceId] = useState<string>("");
	const [newName, setNewName] = useState<string>("");
	const [nameEdited, setNameEdited] = useState<boolean>(false);
	const [facets, setFacets] = useState<FacetFlags>(() => ({ ...DEFAULT_FACETS }));
	const [creativePrefix, setCreativePrefix] = useState<string>("");
	const [errors, setErrors] = useState<FormErrors>({});
	const [clonedId, setClonedId] = useState<string | null>(null);
	const [clonedName, setClonedName] = useState<string | null>(null);

	const sources: readonly SourceCampaign[] = useMemo(
		() => listQuery.data?.items ?? [],
		[listQuery.data],
	);

	const selectedSource: SourceCampaign | undefined = useMemo(
		() => sources.find((c) => c.id === sourceId),
		[sources, sourceId],
	);

	// Auto-fill the name field the first time the user picks a source, unless
	// they have already typed something manually. Switching sources afterwards
	// only overwrites the name if the field is in its "untouched" state.
	useEffect(() => {
		if (!selectedSource) return;
		if (nameEdited) return;
		setNewName(defaultClonedName(selectedSource.name));
	}, [selectedSource, nameEdited]);

	const toggleFacet = (id: FacetId) =>
		setFacets((prev) => ({ ...prev, [id]: !prev[id] }));

	const handleNameChange = (v: string) => {
		setNameEdited(true);
		setNewName(v);
	};

	const validate = (): FormErrors => {
		const next: FormErrors = {};
		const srcErr = validateSourceId(sourceId);
		if (srcErr) next.source = srcErr;
		const nameErr = validateNewName(newName);
		if (nameErr) next.name = nameErr;
		const prefixErr = validateCreativePrefix(creativePrefix);
		if (prefixErr) next.creativePrefix = prefixErr;
		return next;
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next = validate();
		setErrors(next);
		if (Object.keys(next).length > 0) return;

		cloneMut.mutate(
			{
				sourceId,
				newName: newName.trim(),
				facets,
				creativePrefix: creativePrefix.trim() || undefined,
			},
			{
				onSuccess: (data) => {
					setClonedId(data.campaign.id);
					setClonedName(data.campaign.name);
				},
			},
		);
	};

	const handleReset = () => {
		setSourceId("");
		setNewName("");
		setNameEdited(false);
		setFacets({ ...DEFAULT_FACETS });
		setCreativePrefix("");
		setErrors({});
		setClonedId(null);
		setClonedName(null);
	};

	const isSubmitting = cloneMut.isPending;
	const serverError = cloneMut.error?.message;

	return (
		<main
			data-testid="campaign-clone-page"
			className="max-w-7xl mx-auto px-6 py-6"
		>
			{/* Breadcrumb (topbar renders the global one; this mirrors current page). */}
			<nav
				aria-label="Breadcrumb"
				className="flex items-center gap-2 text-sm text-zinc-500 mb-4"
			>
				<Link to="/" className="hover:text-zinc-700">
					Dashboard
				</Link>
				<span className="text-zinc-300">/</span>
				<Link to="/campaign-dashboard" className="hover:text-zinc-700">
					Campaigns
				</Link>
				<span className="text-zinc-300">/</span>
				<span className="text-zinc-900 font-medium">Clone Campaign</span>
			</nav>

			{/* Page header */}
			<header className="flex items-start justify-between mb-6">
				<div>
					<h1 className={HEADLINE}>Clone Campaign</h1>
					<p className={SUBTITLE}>
						Duplicate an existing campaign, pick what to copy, and
						optionally rename it.
					</p>
				</div>
			</header>

			{/* Success panel — shown after a successful clone. */}
			{clonedId && clonedName ? (
				<div
					className={`${CARD} mb-6 border-l-4 border-l-emerald-500`}
					role="status"
					data-testid="campaign-clone-success"
				>
					<div className="flex items-start justify-between gap-4">
						<div>
							<p className={CAPTION_LABEL}>Cloned</p>
							<h2 className="text-base font-medium text-zinc-900 mt-2">
								{clonedName}
							</h2>
							<p className="text-sm text-zinc-500 mt-1">
								Campaign id{" "}
								<span className="font-mono text-zinc-700">{clonedId}</span>
							</p>
						</div>
						<div className="flex items-center gap-2">
							<Link
								to="/campaign-dashboard"
								search={{ highlight: clonedId }}
								className={SECONDARY_BTN}
								data-testid="campaign-clone-view-link"
							>
								View in dashboard
							</Link>
							<button
								type="button"
								className={PRIMARY_BTN}
								onClick={handleReset}
								data-testid="campaign-clone-again"
							>
								Clone another
							</button>
						</div>
					</div>
				</div>
			) : null}

			<form
				onSubmit={handleSubmit}
				className={`${CARD} flex flex-col gap-6`}
				data-testid="campaign-clone-form"
				aria-label="Clone campaign form"
				noValidate
			>
				{/* Section 1 — source picker */}
				<div>
					<label htmlFor="campaign-clone-source" className={LABEL}>
						Source campaign
					</label>
					<SourcePicker
						value={sourceId}
						onChange={(id) => {
							setSourceId(id);
							setErrors((prev) => ({ ...prev, source: undefined }));
						}}
						items={sources}
						loading={listQuery.isLoading}
						error={errors.source}
					/>
					{errors.source ? (
						<p className={ERROR_TEXT} data-testid="campaign-clone-source-error">
							{errors.source}
						</p>
					) : listQuery.error ? (
						<p className={ERROR_TEXT}>
							Could not load campaigns: {listQuery.error.message}
						</p>
					) : (
						<p className={HELPER}>
							{sources.length} campaign{sources.length === 1 ? "" : "s"} available
						</p>
					)}
				</div>

				{/* Section 2 — new name */}
				<div>
					<label htmlFor="campaign-clone-name" className={LABEL}>
						New name
					</label>
					<input
						id="campaign-clone-name"
						type="text"
						className={errors.name ? INPUT_ERR : INPUT}
						value={newName}
						onChange={(e) => handleNameChange(e.currentTarget.value)}
						placeholder="Copy of Summer Launch"
						data-testid="campaign-clone-name-input"
						aria-invalid={errors.name ? "true" : "false"}
						maxLength={256}
					/>
					{errors.name ? (
						<p className={ERROR_TEXT} data-testid="campaign-clone-name-error">
							{errors.name}
						</p>
					) : (
						<p className={HELPER}>
							Defaults to &ldquo;Copy of &lt;source&gt;&rdquo; if you leave
							it untouched.
						</p>
					)}
				</div>

				{/* Section 3 — facet checkboxes */}
				<div>
					<p className={LABEL}>What to copy</p>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
						{CLONE_FACETS.map((f) => (
							<FacetCheckbox
								key={f.id}
								id={f.id}
								label={f.label}
								checked={facets[f.id]}
								onChange={() => toggleFacet(f.id)}
							/>
						))}
					</div>
					<p className={HELPER}>
						All on by default. Untick anything you want a clean slate for.
					</p>
				</div>

				{/* Section 4 — creative prefix (only when Creatives is on) */}
				{facets.creatives ? (
					<div>
						<label htmlFor="campaign-clone-prefix" className={LABEL}>
							Creative name prefix{" "}
							<span className="font-normal text-zinc-400">(optional)</span>
						</label>
						<input
							id="campaign-clone-prefix"
							type="text"
							className={errors.creativePrefix ? INPUT_ERR : INPUT}
							value={creativePrefix}
							onChange={(e) => setCreativePrefix(e.currentTarget.value)}
							placeholder="e.g. Q3-"
							data-testid="campaign-clone-prefix-input"
							aria-invalid={errors.creativePrefix ? "true" : "false"}
							maxLength={64}
						/>
						{errors.creativePrefix ? (
							<p className={ERROR_TEXT}>{errors.creativePrefix}</p>
						) : (
							<p className={HELPER}>
								Prepended to each copied creative&apos;s name.
							</p>
						)}
					</div>
				) : null}

				{/* Server error line */}
				{serverError ? (
					<p
						className="text-sm text-red-600"
						role="alert"
						data-testid="campaign-clone-server-error"
					>
						{serverError}
					</p>
				) : null}

				{/* Footer — Cancel left, Clone right, per DESIGN.md forms spec */}
				<div className="flex justify-end gap-3 pt-2 border-t border-zinc-100">
					<button
						type="button"
						className={SECONDARY_BTN}
						onClick={handleReset}
						disabled={isSubmitting}
						data-testid="campaign-clone-cancel"
					>
						Reset
					</button>
					<button
						type="submit"
						className={PRIMARY_BTN}
						disabled={isSubmitting || listQuery.isLoading}
						data-testid="campaign-clone-submit"
					>
						{isSubmitting ? "Cloning." : "Clone campaign"}
					</button>
				</div>
			</form>
		</main>
	);
}

export default CampaignClonePage;
